import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./style.module.scss";
import bgImage from "../assets/zoibill_bg.png";
import poweredByLogo from "../assets/optenics_logo_small.png";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { EnvelopeIcon, LockIcon, Eye, EyeSlash, X } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import DOMPurify from "dompurify";
import ReCAPTCHA from "react-google-recaptcha";
import ReactMarkdown from "react-markdown";

// Terms and Conditions content
const termsContent = `
# Terms and Conditions

Last Updated: August 28, 2025

By clicking "Login" or accessing our services, you agree to be bound by these Terms and Conditions ("Terms"). These Terms govern your use of the Optenics platform and any applications or services provided by {Company Name} (collectively, the "Services"). If you do not agree to these Terms, please do not use the Services.

## 1. Acceptance of Terms
By accessing or using the Services, you confirm that you are at least 18 years old, have the legal capacity to enter into agreements, and agree to comply with these Terms. If you are using the Services on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.

## 2. Account Creation and Responsibilities
- **Account Security**: You are responsible for maintaining the confidentiality of your account credentials, including your password and any authentication tokens. You agree to notify us immediately of any unauthorized access or use of your account.
- **Accurate Information**: You agree to provide accurate, current, and complete information during account creation and to update it as necessary.
- **Usage Restrictions**: You may not use the Services for any illegal or unauthorized purpose, including but not limited to violating applicable laws, infringing intellectual property, or attempting to gain unauthorized access to our systems.

## 3. Use of Services
- **Optenics Platform**: The Optenics platform provides user and organization management for various applications. You agree to use the Services only for authorized purposes, as permitted by your account access and the specific application features.
- **Prohibited Actions**: You may not reverse-engineer, decompile, or attempt to extract the source code of the Services. You may not use the Services to transmit malicious code, spam, or content that violates third-party rights.

## 4. Payment and Subscriptions
- **Fees**: Certain features of the Services may require payment. You agree to pay all applicable fees as outlined during the subscription or signup process. All payments are non-refundable unless otherwise specified.
- **Compliance**: If using payment processing features, you agree to comply with all applicable regulations and requirements set by our payment gateway partners or relevant authorities.

## 5. Third-Party Integrations
- The Services may integrate with third-party platforms. You are responsible for ensuring your use of third-party services complies with their respective terms and conditions.
- {Company Name} is not liable for any issues arising from third-party services, including data accuracy or service interruptions.

## 6. Intellectual Property
All content, trademarks, and intellectual property within the Services are owned by {Company Name} or its licensors. You are granted a non-exclusive, non-transferable license to use the Services for their intended purpose. You may not copy, modify, or distribute any part of the Services without prior written consent.

## 7. Privacy
Your use of the Services is subject to our Privacy Policy, available at {Privacy Policy URL}. By using the Services, you consent to the collection, use, and sharing of your information as described in the Privacy Policy.

## 8. Termination
We reserve the right to suspend or terminate your access to the Services at our discretion, including for violation of these Terms, non-payment, or suspected fraudulent activity. Upon termination, your right to use the Services will cease immediately.

## 9. Limitation of Liability
To the fullest extent permitted by law, {Company Name} shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Services. The Services are provided "as is" without warranties of any kind, express or implied.

## 10. Indemnification
You agree to indemnify and hold harmless {Company Name}, its affiliates, and employees from any claims, losses, or damages arising from your use of the Services or violation of these Terms.

## 11. Governing Law
These Terms shall be governed by the laws of {Jurisdiction}, without regard to conflict of law principles. Any disputes shall be resolved in the courts of {Jurisdiction}.

## 12. Changes to Terms
We may update these Terms from time to time. You will be notified of significant changes via email or through the Services. Continued use of the Services after changes constitutes acceptance of the updated Terms.

## 13. Contact Us
For questions or concerns about these Terms, please contact us at {Support Email}.

By clicking "Login," you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
`;

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({ email: "", password: "", recaptcha: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    companyName: "Optenics",
    privacyPolicyUrl: "https://optenics.com/privacy-policy/",
    supportEmail: "support@optenics.com",
    supportPhone: "",
    jurisdiction: "Nevada, USA",
  });

  // Compute reCAPTCHA site key
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "your-recaptcha-site-key";

  // Fetch company info (e.g., from Optenics backend)
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await fetch("/api/organization/info", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await response.json();
        setCompanyInfo({
          companyName: data.name || "Optenics",
          privacyPolicyUrl: data.privacyPolicyUrl || "https://optenics.com/privacy",
          supportEmail: data.supportEmail || "support@optenics.com",
          supportPhone: data.supportPhone || "+1-800-555-1234",
          jurisdiction: data.jurisdiction || "Delaware, USA",
        });
      } catch (err) {
        console.error("Failed to fetch company info:", err);
      }
    };

    if (localStorage.getItem("token")) {
      fetchCompanyInfo();
    }
  }, []);

  const sanitizeInput = (value: string) => {
    return DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: {},
    }).trim();
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    if (id === "email" && !validateEmail(sanitizedValue) && sanitizedValue !== "") {
      setErrorMessage("Please enter a valid email address");
    } else {
      setErrorMessage("");
    }
    setFormValues((prev) => ({ ...prev, [id]: sanitizedValue }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setFormValues((prev) => ({ ...prev, recaptcha: token || "" }));
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    if (!formValues.email || !formValues.password) {
      setErrorMessage("Email and password are required");
      setIsSubmitting(false);
      return;
    }

    if (!validateEmail(formValues.email)) {
      setErrorMessage("Please enter a valid email address");
      setIsSubmitting(false);
      return;
    }

    if (showRecaptcha && !formValues.recaptcha) {
      setErrorMessage("Please complete the CAPTCHA");
      setIsSubmitting(false);
      return;
    }

    if (!hasAcceptedTerms) {
      setErrorMessage("You must view and acknowledge the Terms and Conditions to log in");
      setShowModal(true);
      setIsSubmitting(false);
      return;
    }

    try {
      await login(formValues.email, formValues.password, rememberMe, "Optenics");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      const message = sanitizeInput(err.message || "Login failed. Please check your credentials.");
      if (message.includes("locked") || message.includes("too many attempts")) {
        setShowRecaptcha(true);
      }
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Replace placeholders in Terms and Conditions
  const renderTermsContent = () => {
    let content = termsContent;
    content = content.replace(/{Company Name}/g, companyInfo.companyName);
    content = content.replace(/{Privacy Policy URL}/g, companyInfo.privacyPolicyUrl);
    content = content.replace(/{Support Email}/g, companyInfo.supportEmail);
    content = content.replace(/{Support Phone Number}/g, companyInfo.supportPhone);
    content = content.replace(/{Jurisdiction}/g, companyInfo.jurisdiction);
    return content;
  };

  return (
    <div className={styles.pageWrapper}>
      <Header isLoggedIn={false} />
      <div className={styles.pageContent}>
        <div className={styles.leftPanel} style={{ backgroundImage: `url(${bgImage})` }}>
          <div className={styles.brandingBox}>
            <h1>Welcome Back!</h1>
            <p>Manage system users, roles, and permissions.</p>
            <p>Configure global settings, integrations, and security policies.</p>
            <p>Configure and manage modules available for each application.</p>
          </div>
          <div className={styles.poweredBy}>
            <span>Powered by</span>
            <a href="https://optenics.com" target="_blank" rel="noreferrer">
              <img src={poweredByLogo} alt="Optenics" />
            </a>
          </div>
        </div>

        <div className={styles.rightPanel}>
          <div className={styles.loginContainer}>
            <h2>Login To Your Account</h2>
            <p>Enter your details to login.</p>

            {errorMessage && <div className={styles.error}>{errorMessage}</div>}

            <form onSubmit={handleLogin}>
              <div className={styles.inputGroup}>
                <EnvelopeIcon className={styles.inputIcon} />
                <input
                  type="email"
                  id="email"
                  placeholder="Enter your email"
                  value={formValues.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className={styles.inputGroup}>
                <LockIcon className={styles.inputIcon} />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="Enter your password"
                  value={formValues.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.togglePassword}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {showRecaptcha && (
                <div className={styles.recaptcha}>
                  <ReCAPTCHA
                    sitekey={recaptchaSiteKey}
                    onChange={handleRecaptchaChange}
                  />
                </div>
              )}

              <div className={styles.options}>
                <label>
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  <span className={styles.checkboxLabel}>Remember Me</span>
                </label>
              </div>

              <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                {isSubmitting ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className={styles.agreement}>
              By clicking login, you agree to our{" "}
              <span
                onClick={() => setShowModal(true)}
                style={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Terms & Conditions
              </span>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.modalCloseButton}
              onClick={() => {
                setHasAcceptedTerms(true);
                setShowModal(false);
              }}
            >
              <X size={24} color="#1a1a1a" />
            </button>
            <h3 className={styles.modalTitle}>Terms & Conditions</h3>
            <ReactMarkdown>{renderTermsContent()}</ReactMarkdown>
          </div>
        </div>
      )}

      <Footer isLoggedIn={false} />
    </div>
  );
};

export default LoginPage;
